
import { defineComponent, computed, onMounted, ref, watch } from 'vue';
import { useDicomStore } from '@/store/dicom'
import { useUsersStore } from '@/store/users'
import { useLogStore } from '@/store/log';
import { useTimerStore } from '@/store/timer'
import { usePopupStore } from '@/store/popup'
import { useReadingResultStore } from '@/store/readingResult'
import VueSlider from 'vue-slider-component'
import { nextTick } from 'vue';
import 'vue-slider-component/theme/antd.css'
import { debounce } from 'lodash'
import { recordLog } from '@/api'
import { useViewerStore } from '@/store/viewer'

export default defineComponent({
  components: {
    VueSlider
  },
  setup() {
    const dicomStore = useDicomStore();
    const usersStore = useUsersStore();
    const popupStore = usePopupStore();
    const timerStore = useTimerStore();
    const readingResultStore = useReadingResultStore();
    const userId = computed(() => usersStore.getLoginUser?.data.user_id);
    const visualizationOption = computed(() => dicomStore.getVisualizationOption);
    const withCAD = computed(() => usersStore.getWithCAD);
    const dicomFilesList = computed(() => dicomStore.getDicomFilesList);
    const dicomInfo = computed(() => dicomStore.getTargetDicomInfo);
    const dicomFile = computed(() => dicomStore.getTargetDicomFile);
    const showLoadingBox = computed(() => dicomStore.getLoadingState);
    const scoreLoading = computed(() => dicomStore.getScoreLoadingState);
    const completeScore = ref(0);
    const wc = ref(0);
    const ww = ref(0);
    const tube = ref(0.01);
    const iw = ref(0.01);
    const windowCenterMin = computed(() => dicomInfo.value?.window_center_min || 0);
    const windowCenterMax = computed(() => dicomInfo.value?.window_center_max || 1);
    const windowWidthMin = computed(() => dicomInfo.value?.window_width_min || 0);
    const windowWidthMax = computed(() => dicomInfo.value?.window_width_max || 1);
    const maskCheck = ref(true);
    const heatMapCheck = ref(true); 
    const probabilityCheck = ref(true); 
    const comboboxOption = ref([{ name: 'Complete', selected: true }, { name: 'Incomplete', selected: false }]);
    const comboboxSelected = ref('Complete');
    const comboboxOpen = ref(false);
    const savedCompleteScore = ref(false);
    const fileLoaded = computed(() => useViewerStore().getFileLoadState);
    const imageLoading = computed(() => dicomStore.getImageLoadingState);

    const setWindowOption = () => {
      dicomStore.setWindowOption(wc.value, ww.value);
    }

    const setExtraOption = () => {
      dicomStore.setExtraOption(tube.value, iw.value);
    }

    const switchViewer = () => {
      if(!fileLoaded.value) {
        useViewerStore().setFileLoadState(true);
      }
    }

    const setHeatmapOption = (idx: number) => {
      for(let i=0; i<comboboxOption.value.length; i++) {
        const option = comboboxOption.value[i];
        option.selected = false;
        if(idx == i) {
          option.selected = true;
          comboboxSelected.value = option.name;
        }
      }
      dicomStore.setHeatmapOption(idx, comboboxSelected.value);
    }

    const toggleMaskCheck = (checked: boolean) => {
      maskCheck.value = checked;
      dicomStore.setMaskCheck(maskCheck.value);
    };
   
    const toggleHeatMapCheck = (checked: boolean) => {
      heatMapCheck.value = checked;
      dicomStore.setHeatMapCheck(heatMapCheck.value);
    };
   
    const toggleProbabilityCheck = (checked: boolean) => {
      probabilityCheck.value = checked;
      dicomStore.setProbabilityCheck(probabilityCheck.value);
    };

    const validateScore = () => {
      if (completeScore.value !== null) {
        if (completeScore.value < 0) {
          completeScore.value = 0;
        } else if (completeScore.value > 100) {
          completeScore.value = 100;
        }
      }
    }

    const visualization = async (): Promise<void> => {
      dicomStore.setLoading(true);
      await dicomStore.visualization();
    }

    const toggleCombobox = () => {
      comboboxOpen.value = !comboboxOpen.value;
    }

    const initializeOptions = () => {
      if(dicomFile.value?.unique_filename) {
        dicomStore.initializeOptions();
        toggleMaskCheck(true);
        toggleHeatMapCheck(true);
        toggleProbabilityCheck(true);
      }
    }

    const saveScore = async(): Promise<void> => {
      if(!savedCompleteScore.value && Object.keys(dicomFile.value).length > 0 && !dicomFile.value.is_reviewed && 
        (completeScore.value?.toString() !== '') && completeScore.value?.toString() !== undefined) {
        useDicomStore().setScoreLoading(true);
        useDicomStore().setFileSelectDisabled(true);
        timerStore.stopTimer();
        popupStore.togglePopup('completeScore', true);
        savedCompleteScore.value = true;
        dicomStore.setCompleteScore(completeScore.value);
        await readingResultStore.insertReadingResult();
        readingResultStore.setShowNewIcon(true);
        await dicomStore.getDicomList();
        useDicomStore().setScoreLoading(false);
        useDicomStore().setFileSelectDisabled(false);
      }
    }

    const resetScore = async(): Promise<void> => {
      useDicomStore().setImageLoading(true);
      useDicomStore().setScoreLoading(true);
      useDicomStore().setFileSelectDisabled(true);
      await readingResultStore.deleteReadingResultList();
      await dicomStore.getDicomList();
      useDicomStore().setImageLoading(false);
      useDicomStore().setScoreLoading(false);
      useDicomStore().setFileSelectDisabled(false);
      completeScore.value = dicomFile.value.score;
    }

    const setDicomInfoValue = () => {
      wc.value = dicomInfo.value?.window_center ?? 0;
      ww.value = dicomInfo.value?.window_width ?? 0;
      tube.value = dicomInfo.value?.tube_threshold ?? 0.01;
      iw.value = dicomInfo.value?.image_weight ?? 0.01;
      setHeatmapOption(dicomInfo.value?.select_heatmap_idx ?? 0);
    }

    watch(dicomFilesList, (newVal) => {
      const found = dicomFilesList.value.find(item => item.unique_id === dicomFile.value.unique_id);
      if(found) {
        dicomFile.value.is_reviewed = found?.is_reviewed;
      }
    });

    watch(wc, (newVal) => {
      dicomStore.setTargetDicomInfo('window_center', newVal)
    })

    watch(ww, (newVal) => {
      dicomStore.setTargetDicomInfo('window_width', newVal)
    })

    watch(tube, (newVal) => {
      dicomStore.setTargetDicomInfo('tube_threshold', newVal)
    })

    watch(iw, (newVal) => {
      dicomStore.setTargetDicomInfo('image_weight', newVal)
    })

    // watch([visualizationOption], (newVal) => {
      // completeScore.value = dicomFile.value.score;
      // maskCheck.value = visualizationOption.value?.mask_check;
      // heatMapCheck.value = visualizationOption.value?.heatmap_check;
      // probabilityCheck.value = visualizationOption.value?.probability_map_check;
      // setDicomInfoValue();
    // });

    watch(dicomFile, (newVal) => {
      completeScore.value = dicomFile.value.score;
      savedCompleteScore.value = false;
      toggleMaskCheck(true);
      toggleHeatMapCheck(true);
      toggleProbabilityCheck(true);
    });

    watch(dicomInfo, (newVal) => {
      setDicomInfoValue();
    });

    watch(
      () => dicomFile.value.is_reviewed, 
      (newVal) => {
        savedCompleteScore.value = newVal;
      }
    );

    onMounted(() => {
      dicomStore.initializeDefaults();
      dicomStore.resetPendingCount();
    });

    return {
      floatMask: [0.01, 1],
      windowCenterMin,
      windowCenterMax,
      windowWidthMin,
      windowWidthMax,
      maskCheck,
      heatMapCheck,
      probabilityCheck,
      comboboxOption,
      comboboxSelected,
      comboboxOpen,
      toggleCombobox,
      dicomInfo,
      dicomFile,
      wc,
      ww,
      tube,
      iw,
      initializeOptions,
      showLoadingBox,
      scoreLoading,
      completeScore,
      withCAD,
      saveScore,
      validateScore,
      savedCompleteScore,
      visualizationOption,
      toggleMaskCheck,
      toggleHeatMapCheck,
      toggleProbabilityCheck,
      visualization,
      setWindowOption,
      setExtraOption,
      setHeatmapOption,
      dicomFilesList,
      resetScore,
      switchViewer,
      imageLoading,
      fileLoaded
    };
  },
});
